import React, {useEffect, useState, useMemo} from 'react';
import {
    Card,
    ChoiceList,
    TextField,
    EmptySearchResult,
    Filters,
    IndexTable,
    Page,
    Pagination,
    useIndexResourceState
} from "@shopify/polaris";

import debounce from "lodash/debounce";
import {AxiosError} from "axios";

import ConsignmentService, {
    Consignment,
    ConsignmentSearchParams,
    ConsignmentStatus
} from "../services/ConsignmentService";
import {PaginatableResults} from "../services/CommonModel"

import DateRangeDescription from "../components/DateRangeDescription"
import ConsignmentStatusBadge from "../components/ConsignmentStatusBadge"
import ConsignmentWaypointDescription from "../components/ConsignmentWaypointDescription"
import ConsignmentCancelModal from "../components/ConsignmentCancelModal"
import AxiosErrorBanner from "../components/AxiosErrorBanner"
import {isEmpty} from "../util/LangUtil";
import OrderItemsPopover from "../components/OrderItemsPopover";

const resourceIDResolver = (consignment: Consignment) => {
    return consignment.consignmentRef;
};

export default function MyDeliveries() {
    const [ loading, setLoading ] = useState(false);
    const [ showCancelConsignmentModal, setShowCancelConsignmentModal ] = useState(false);
    const [ consignments, setConsignments ] = useState<PaginatableResults<Consignment>>(new PaginatableResults<Consignment>(null));
    const [ axiosError, setAxiosError ] = useState<AxiosError | null>(null);
    // @ts-ignore - typescript fails with a type error that I could not figure out - TS2345: Argument of type 'Consignment[]' is not assignable to parameter of type '{ [key: string]: unknown; }[]'.
    const { selectedResources, allResourcesSelected, handleSelectionChange } = useIndexResourceState(consignments.results,
        {resourceIDResolver});

    const fetchConsignments = (offset: Number) => {
        if (loading) {
            return;
        }
        setLoading(true);
        const searchParams : ConsignmentSearchParams = {
            offset: offset,
            salesOrderRefLike: filterSalesOrderRefLike,
            consignmentRefLike: filterConsignmentRefLike,
            status: filterConsignmentStatus
        }
        ConsignmentService.search(
            searchParams,
            response => setConsignments(response),
            setAxiosError,
            () => {
                setLoading(false);
            }
        );
    };

    //
    // Sales Order Ref - Debounced - https://kyleshevlin.com/debounce-and-throttle-callbacks-with-react-hooks
    const [salesOrderRefLike, setSalesOrderRefLike] = useState<string | undefined>(undefined);
    const [filterSalesOrderRefLike, setFilterSalesOrderRefLike] = useState<string | undefined>(undefined); // debounced value
    const debounceSetFilterSalesOrderRefLike = useMemo(() =>
        debounce(value => setFilterSalesOrderRefLike(value), 500),
        []
    );
    useEffect(() => {
        debounceSetFilterSalesOrderRefLike(salesOrderRefLike);
    }, [salesOrderRefLike, debounceSetFilterSalesOrderRefLike]);
    //


    //
    // Consignment Ref - Debounced - https://kyleshevlin.com/debounce-and-throttle-callbacks-with-react-hooks
    const [consignmentRefLike, setConsignmentRefLike] = useState<string | undefined>(undefined);
    const [filterConsignmentRefLike, setFilterConsignmentRefLike] = useState<string | undefined>(undefined); // debounced value
    const debounceSetFilterConsignmentRefLike = useMemo(() =>
        debounce(value => setFilterConsignmentRefLike(value), 500),
        []
    );
    useEffect(() => {
        debounceSetFilterConsignmentRefLike(consignmentRefLike);
    }, [consignmentRefLike, debounceSetFilterConsignmentRefLike]);


    const [filterConsignmentStatus, setFilterConsignmentStatus] = useState<ConsignmentStatus[] | undefined>(undefined);
    const clearAllFilters = () => {
        setFilterConsignmentStatus(undefined);
        setFilterSalesOrderRefLike(undefined);
        setFilterConsignmentRefLike(undefined);
    }

    useEffect(() => {
        fetchConsignments(0);
    }, [filterConsignmentStatus, filterSalesOrderRefLike, filterConsignmentRefLike]);  // eslint-disable-line react-hooks/exhaustive-deps


    const filters = [
        {
            key: 'consignmentStatus',
            label: 'Delivery status',
            filter: (
                <ChoiceList
                    title="Delivery status"
                    titleHidden
                    choices={[
                        {label: 'Unassigned', value: ConsignmentStatus.UNASSIGNED},
                        {label: 'Assigned', value: ConsignmentStatus.ASSIGNED},
                        {label: 'Started', value: ConsignmentStatus.STARTED},
                        {label: 'Completed', value: ConsignmentStatus.COMPLETED},
                        {label: 'Cancelled', value: ConsignmentStatus.CANCELLED},
                    ]}
                    selected={filterConsignmentStatus || []}
                    // @ts-ignore
                    onChange={values => setFilterConsignmentStatus(values)}
                    allowMultiple
                />
            ),
            shortcut: true,
        },
        {
            key: 'filterConsignmentRefLike',
            label: 'Delivery Ref',
            filter: (
                <TextField
                    label="Delivery Ref"
                    value={consignmentRefLike}
                    onChange={setConsignmentRefLike}
                    autoComplete="off"
                    labelHidden
                />
            ),
            shortcut: true,
        }
    ];

    // @ts-ignore
    const appliedFilters = [];
    if (filterConsignmentStatus && !isEmpty(filterConsignmentStatus)) {
        filterConsignmentStatus.forEach(status => {
            appliedFilters.push({
                key: `filterConsignmentStatus${status}`,
                label: `Status: ${status}`,
                onRemove: () => {
                    setFilterConsignmentStatus(filterConsignmentStatus.filter(s => status !== s))
                }
            });
        });
    }
    if (filterConsignmentRefLike && !isEmpty(filterConsignmentRefLike)) {
        appliedFilters.push({
            key: "filterConsignmentRefLike",
            label: `Delivery Ref: ${filterConsignmentRefLike}`,
            onRemove: () => {
                setFilterConsignmentRefLike(undefined)
            }
        });
    }

    return (
        <Page
            fullWidth={true}
            title="My Deliveries"
        >
            <Card>
                <div style={{padding: '16px', display: 'flex'}}>
                    <div style={{flex: 1}}>
                        <Filters
                            queryValue={salesOrderRefLike || ""}
                            onQueryChange={setSalesOrderRefLike}
                            onQueryClear={() => setSalesOrderRefLike(undefined)}
                            filters={filters}
                            // @ts-ignore
                            appliedFilters={appliedFilters}
                            onClearAll={clearAllFilters}
                        />
                    </div>
                </div>
                <AxiosErrorBanner
                    axiosError={axiosError}
                    onDismiss={() => setAxiosError(null)}
                />
                <IndexTable
                    resourceName={{
                        singular: 'delivery',
                        plural: 'deliveries',
                    }}
                    loading={loading}
                    itemCount={consignments.results.length}
                    selectedItemsCount={
                        allResourcesSelected ? 'All' : selectedResources.length
                    }
                    onSelectionChange={handleSelectionChange}
                    promotedBulkActions={[{
                        content: 'Cancel deliveries',
                        onAction: () => setShowCancelConsignmentModal(true),
                    }]}
                    emptyState={(
                        <EmptySearchResult
                            title={'No deliveries found'}
                            description={'Try changing the filters or search order #'}
                            withIllustration
                        />
                    )}
                    headings={[
                        {title: 'Order #'},
                        {title: 'SubTrux Ref'},
                        {title: 'Delivery Date'},
                        {title: 'Customer'},
                        {title: 'Items'},
                        {title: 'Pickup Location'},
                        {title: 'Deliver To'},
                        {title: 'Delivery Status'},
                    ]}
                >
                    {consignments.results.map(
                        (consignment, index) => (
                            <IndexTable.Row
                                id={consignment.consignmentRef}
                                key={consignment.consignmentRef}
                                selected={selectedResources.includes(consignment.consignmentRef)}
                                position={index}
                            >
                                <IndexTable.Cell>{consignment.salesOrderRef}</IndexTable.Cell>
                                <IndexTable.Cell>{consignment.consignmentRef}</IndexTable.Cell>
                                <IndexTable.Cell><DateRangeDescription from={consignment.delivery.from} to={consignment.delivery.to} /></IndexTable.Cell>
                                <IndexTable.Cell>{consignment.customer ? consignment.customer.name : ""}</IndexTable.Cell>
                                <IndexTable.Cell><OrderItemsPopover orderItems={consignment.freight} /></IndexTable.Cell>
                                <IndexTable.Cell><ConsignmentWaypointDescription waypoint={consignment.pickup} /></IndexTable.Cell>
                                <IndexTable.Cell><ConsignmentWaypointDescription waypoint={consignment.delivery} /></IndexTable.Cell>
                                <IndexTable.Cell><ConsignmentStatusBadge status={consignment.status} /></IndexTable.Cell>
                            </IndexTable.Row>
                        ),
                    )}
                </IndexTable>
                {consignments?.maxPages() > 1 &&
                <div className="center-content">
                    <Pagination
                        label={`Page ${consignments?.pageNo()} of ${consignments?.maxPages()}`}
                        hasPrevious={consignments?.hasPrevious()}
                        onPrevious={() => fetchConsignments(consignments.offset - consignments.limit)}
                        hasNext={consignments?.hasNext()}
                        onNext={() => fetchConsignments(consignments.offset + consignments.limit)}
                    />
                </div>}
                <ConsignmentCancelModal
                    visible={showCancelConsignmentModal}
                    consignmentRefs={selectedResources}
                    onClose={() => setShowCancelConsignmentModal(false)}
                    onCancelComplete={() => {
                        setShowCancelConsignmentModal(false);
                        fetchConsignments(consignments.offset);
                    }}
                />
            </Card>
        </Page>
    );
}